/**
 * Dynamically loads a remote module.
 *
 * Loads the remoteEntry script if not already loaded, initializes Module Federation sharing,
 * and retrieves the exposed module from the container.
 *
 * @param {string} remoteEntryUrl - URL of the remote entry file.
 * @param {string} remoteName - The remote scope name.
 * @param {string} exposedModule - The exposed module key (e.g., './HomePageMap').
 * @returns {Promise<any>} A promise that resolves to the remote module.
 */


import React from "react";

export async function loadRemoteModule(
    remoteEntryUrl: string,
    remoteName: string,
    exposedModule: string
): Promise<any> {
    // Dynamically load the remoteEntry if not already done.
    await new Promise<void>((resolve, reject) => {
        if ((window as any)[remoteName]) {
            return resolve();
        }
        const script = document.createElement('script');
        script.src = remoteEntryUrl;
        script.type = 'text/javascript';
        script.async = true;
        script.onload = () => {
            console.log(`Remote entry ${remoteEntryUrl} for ${remoteName} loaded`);
            resolve();
        };
        script.onerror = () => {
            reject(new Error(`Failed to load remote entry: ${remoteEntryUrl}`));
        };
        document.head.appendChild(script);
    });

    // Initialize sharing.
    // @ts-ignore
    await __webpack_init_sharing__('default');
    // @ts-ignore
    const container = (window as any)[remoteName];
    // Initialize the container to share modules.
    // @ts-ignore
    await container.init(__webpack_share_scopes__.default);
    // Get the module factory.
    // @ts-ignore
    const factory = await container.get(exposedModule);
    return factory();
}

/**
 * Returns a React lazy component that loads a remote module using the provided configuration.
 *
 * @param {object} config - Remote configuration.
 * @param {string} config.remoteEntry - URL of remote entry.
 * @param {string} config.remoteName - Remote scope name.
 * @param {string} config.exposedModule - Exposed module key.
 * @returns {React.LazyExoticComponent<React.ComponentType<any>>} A lazy-loaded component.
 */
export const getRemoteComponent = (config: { remoteEntry: string, remoteName: string, exposedModule: string }) => {
    return React.lazy(async () => {
        const Module = await loadRemoteModule(
            config.remoteEntry,
            config.remoteName,
            config.exposedModule
        );
        // Check if the loaded module has a default export, otherwise assume the module itself is the component.
        const Component = Module && Module.default ? Module.default : Module;
        // Make sure Component is a function or class component.
        if (typeof Component !== 'function') {
            throw new Error("Loaded module is not a valid React component.");
        }
        return { default: Component };
    });
};